import { useCallback, useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ContentCopy } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { useGetProviderEncountersQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function EncountersListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const copy = useCopyToClipboard();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const perPageParam = searchParams.get('perPage');
    return perPageParam ? parseInt(perPageParam, 10) : 20;
  });
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const initialFilters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (!['page', 'perPage', 'sortBy', 'sortOrder'].includes(key)) {
        initialFilters[key] = value;
      }
    });
    return initialFilters;
  });
  const [sortBy, setSortBy] = useState<string>(() => searchParams.get('sortBy') || 'created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() =>
    searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc',
  );

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Add page if not 1
    if (page !== 1) {
      newParams.set('page', page.toString());
    }

    // Add perPage if not 20
    if (perPage !== 20) {
      newParams.set('perPage', perPage.toString());
    }

    if (sortBy !== 'created_at' || sortOrder !== 'desc') {
      newParams.set('sortBy', sortBy);
      newParams.set('sortOrder', sortOrder);
    }

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [page, perPage, filters, sortBy, sortOrder, setSearchParams]);

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Memoized sort change handler
  const handleSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when sort changes
  }, []);

  // Status badge color function
  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Get URL for each row (for shareable links)
  const getRowUrl = useCallback(
    (encounter: any) => {
      return providerId
        ? `/admin-v2/provider/${encodeURIComponent(providerId)}/encounters/${encounter.encounter_id}`
        : `/admin-v2/encounter/${encounter.encounter_id}`;
    },
    [providerId],
  );

  // Get state for each row (not needed for shareable URLs)
  // const getRowState = useCallback(() => {
  //   return {};
  // }, []);

  // Fetch provider's encounters with pagination, filtering, and sorting
  const { data: encountersData, isLoading: encountersLoading } = useGetProviderEncountersQuery(
    {
      providerId: providerId ?? '',
      page,
      perPage,
      status: filters.status,
      createdAfter: filters.created_after,
      createdBefore: filters.created_before,
      sortBy,
      sortOrder,
    },
    {
      skip: !providerId,
    },
  );

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const encounterColumns = [
    {
      key: 'encounter_id' as const,
      label: 'Encounter ID',
      sortable: false,
      render: (value: any) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm break-all text-blue-600">{String(value ?? '')}</span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  copy(String(value), 'Encounter ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'created_at' as const,
      label: 'Created',
      render: (value: any) => formatDateTime(value),
    },
    {
      key: 'scheduled_for' as const,
      label: 'Scheduled',
      render: (value: any) =>
        value ? formatDateTime(value) : <span className="text-sm text-gray-400">—</span>,
    },
    {
      key: 'date_of_service' as const,
      label: 'Date of Service',
      render: (value: any) =>
        value ? (
          <span className="text-sm text-gray-900">{value}</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'signed_at' as const,
      label: 'Signed',
      render: (value: any, row: any) =>
        row?.is_signed && value ? (
          <span className="text-sm text-green-700">{formatDateTime(value)}</span>
        ) : (
          <span className="text-sm text-gray-400">No</span>
        ),
    },
    {
      key: 'status' as const,
      label: 'Status',
      sortable: false,
      render: (value: any) => (
        <Badge color={getStatusBadgeColor(value)}>
          {value ? value.replace('_', ' ') : 'Unknown'}
        </Badge>
      ),
    },
    {
      key: 'encounter_type' as const,
      label: 'Type',
      sortable: false,
      render: (value: any) => <span className="text-sm text-gray-600">{value || 'N/A'}</span>,
    },
    {
      key: 'input_documents' as const,
      label: 'Input Docs',
      sortable: false,
      render: (value: any) => (
        <span className={`text-sm font-medium ${value === 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {value || 0}
        </span>
      ),
    },
    {
      key: 'note_id' as const,
      label: 'Note',
      sortable: false,
      render: (value: any) =>
        value ? (
          <span className="text-sm text-green-600">Yes</span>
        ) : (
          <span className="text-sm text-gray-400">No</span>
        ),
    },
  ] as const;

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      key: 'created_after',
      label: 'Created After',
      type: 'text' as const,
    },
    {
      key: 'created_before',
      label: 'Created Before',
      type: 'text' as const,
    },
  ];

  return (
    <FilterableDataTable
      title="Encounters"
      columns={encounterColumns as any}
      data={encountersData?.items || []}
      total={encountersData?.total}
      loading={encountersLoading}
      page={page}
      perPage={perPage}
      filters={filterConfigs}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onPageChange={setPage}
      onPerPageChange={setPerPage}
      onFiltersChange={handleFiltersChange}
      onSortChange={handleSortChange}
      getRowUrl={getRowUrl}
      emptyMessage="No encounters to display"
    />
  );
}
